import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatPrice } from '../../../utils/money';
import type { PaymentMethod } from '../../../types/models/PaymentMethod';
import type { PaymentSource } from '../../../types/models/Payment';
import {
  WALLET_DEPOSIT_FAILED_STATUSES,
  type Wallet,
  type WalletDepositCreateRequest,
} from '../../../types/models/Wallet';
import type {
  ApiResponseWalletDepositCreate,
  ApiResponseWalletDepositShow,
} from '../../../types/api/ApiResponseWallet';
import PaymentSourceChooser from './PaymentSourceChooser';

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once the balance was credited (saved-card top-up) so the parent refetches the wallet. */
  onCredited: () => void;
  wallet: Wallet | null;
  defaultPaymentMethod: PaymentMethod | null;
  walletLoading: boolean;
  walletError: string | null;
}

// Up to 5 integer digits (deposit_max is 5000.00) and at most 2 decimals — the contract's shape.
const AMOUNT_RE = /^\d{1,5}(\.\d{1,2})?$/;

/** Accept a Belgian-style comma decimal and trim; returns the dot-normalised string. */
export function normaliseAmountInput(raw: string): string {
  return raw.trim().replace(',', '.');
}

/**
 * Validate a top-up amount against the wallet's bounds. Returns an error message or `null`.
 * Comparison is numeric for display gating only; the normalised STRING is what gets sent.
 */
export function validateTopUpAmount(raw: string, wallet: Wallet | null): string | null {
  const value = normaliseAmountInput(raw);
  if (value === '') {
    return 'Enter an amount.';
  }
  if (!AMOUNT_RE.test(value)) {
    return 'Enter an amount with at most 2 decimals, e.g. 100.00.';
  }
  if (wallet === null) {
    return null;
  }
  const n = parseFloat(value);
  const min = parseFloat(wallet.deposit_min);
  const max = parseFloat(wallet.deposit_max);
  if (!Number.isNaN(min) && n < min) {
    return `The minimum top-up is ${formatPrice(wallet.deposit_min)}.`;
  }
  if (!Number.isNaN(max) && n > max) {
    return `The maximum top-up is ${formatPrice(wallet.deposit_max)}.`;
  }
  return null;
}

// Saved-card top-ups that Mollie left in flight (`open`/`pending`) are polled here: ~10 attempts,
// 2.5s apart ≈ 25s, then we let the parent refetch and move on (the webhook credits it later).
const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2500;

type Phase = 'idle' | 'submitting' | 'polling' | 'redirecting';

/**
 * "Top up your prepaid balance" — amount + payment source (hosted checkout, or the saved card;
 * the wallet can't pay for itself), then `POST /clients/wallet/deposits` (docs/api-types/wallet.md).
 * A checkout top-up redirects the browser to Mollie and returns to `/payment/return?deposit={id}`;
 * a saved-card top-up settles in the background and is credited inline (or polled until it is).
 */
const TopUpModal: React.FunctionComponent<TopUpModalProps> = ({
  isOpen,
  onClose,
  onCredited,
  wallet,
  defaultPaymentMethod,
  walletLoading,
  walletError,
}) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [amount, setAmount] = useState('');
  const [touched, setTouched] = useState(false);
  const [source, setSource] = useState<PaymentSource>('checkout');
  const [saveCard, setSaveCard] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const [amountServerError, setAmountServerError] = useState<string | null>(null);

  // Hold apiFetch in a ref so a background OIDC token renew mid-poll can't restart the polling loop
  // (its identity changes on every renew — see useApiFetch) while in-flight polls keep a fresh token.
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);
  const activeRef = useRef(true);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setTouched(false);
      setSource('checkout');
      setSaveCard(false);
      setPhase('idle');
      setFormError(null);
      setAmountServerError(null);
    }
  }, [isOpen]);

  const validationError = validateTopUpAmount(amount, wallet);
  const amountError = amountServerError ?? (touched ? validationError : null);
  const normalisedAmount = validationError === null ? normaliseAmountInput(amount) : null;
  const busy = phase !== 'idle';

  const notifyError = useCallback(
    (title: string, description: string) => {
      addNotification({ title, srTitle: 'Error alert', description, variant: 'danger' });
      setDrawerOpen(true);
    },
    [addNotification, setDrawerOpen],
  );

  const creditSuccess = useCallback(
    (creditedAmount: string) => {
      addNotification({
        title: 'Balance credited',
        srTitle: 'Success alert',
        description: `${formatPrice(creditedAmount)} was added to your prepaid balance.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      onCredited();
      onClose();
    },
    [addNotification, setDrawerOpen, onCredited, onClose],
  );

  const pollDeposit = useCallback(
    async (depositId: number, creditedAmount: string) => {
      setPhase('polling');
      for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!activeRef.current) {
          return;
        }
        try {
          const response = await apiFetchRef.current(`/clients/wallet/deposits/${depositId}`);
          const body = (await response.json().catch(() => null)) as ApiResponseWalletDepositShow | null;
          const deposit = body?.status === 'success' ? body.data?.deposit : undefined;
          if (deposit?.credited === true) {
            creditSuccess(deposit.amount);
            return;
          }
          if (deposit && WALLET_DEPOSIT_FAILED_STATUSES.includes(deposit.status)) {
            const message = `The charge on your saved card did not go through (status: ${deposit.status}). Nothing was credited — try another payment method.`;
            setFormError(message);
            notifyError('Top-up failed', message);
            setPhase('idle');
            return;
          }
        } catch {
          // Transient — try again on the next tick.
        }
      }
      if (!activeRef.current) {
        return;
      }
      addNotification({
        title: 'Top-up still processing',
        srTitle: 'Info alert',
        description: `Your ${formatPrice(creditedAmount)} top-up is still being confirmed by the payment provider. It will be credited automatically — refresh your balance in a moment.`,
        variant: 'info',
      });
      setDrawerOpen(true);
      onCredited();
      onClose();
    },
    [addNotification, setDrawerOpen, creditSuccess, notifyError, onCredited, onClose],
  );

  const handleSubmit = useCallback(async () => {
    setTouched(true);
    setFormError(null);
    setAmountServerError(null);
    if (normalisedAmount === null) {
      return;
    }
    setPhase('submitting');
    const payload: WalletDepositCreateRequest = {
      amount: normalisedAmount,
      source: source === 'card' ? 'card' : 'checkout',
    };
    if (source === 'checkout' && saveCard) {
      payload.save_card = true;
    }
    try {
      const response = await apiFetch('/clients/wallet/deposits', { method: 'POST', body: JSON.stringify(payload) });
      // JSend: branch on body.status — errors arrive at HTTP 200 with a fixed `message`.
      const body = (await response.json().catch(() => null)) as ApiResponseWalletDepositCreate | null;

      if (body?.status === 'success' && typeof body.data?.deposit_id === 'number') {
        if (typeof body.data.checkout_url === 'string' && body.data.checkout_url !== '') {
          // Hand off to Mollie; keep the modal open in its redirecting state — the page is unloading.
          setPhase('redirecting');
          window.location.href = body.data.checkout_url;
          return;
        }
        if (body.data.credited === true) {
          creditSuccess(normalisedAmount);
          return;
        }
        if (body.data.source === 'card') {
          await pollDeposit(body.data.deposit_id, normalisedAmount);
          return;
        }
      }

      const message = body?.message ?? 'Could not start the top-up. Please try again.';
      // A validation failure carries per-field detail under data.amount.
      if (typeof body?.data?.amount === 'string') {
        setAmountServerError(body.data.amount);
      }
      setFormError(message);
      notifyError('Could not start top-up', message);
      setPhase('idle');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected network error occurred.';
      setFormError(message);
      notifyError('Could not start top-up', message);
      setPhase('idle');
    }
  }, [apiFetch, normalisedAmount, source, saveCard, creditSuccess, pollDeposit, notifyError]);

  const submitLabel = normalisedAmount !== null ? `Top up ${formatPrice(normalisedAmount)}` : 'Top up';

  return (
    <Modal
      variant="small"
      isOpen={isOpen}
      onClose={busy ? undefined : onClose}
      aria-label="Top up your prepaid balance"
    >
      <ModalHeader
        title="Top up your prepaid balance"
        description={
          wallet
            ? `Top-ups between ${formatPrice(wallet.deposit_min)} and ${formatPrice(wallet.deposit_max)}. A top-up is credited once the payment provider confirms it; no invoice is issued for it.`
            : undefined
        }
      />
      <ModalBody>
        {phase === 'redirecting' ? (
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Redirecting to the payment provider" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Redirecting you to our payment provider — please keep this window open.
              </Content>
            </FlexItem>
          </Flex>
        ) : (
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmit();
            }}
          >
            {formError && <Alert variant="danger" isInline title={formError} />}
            {phase === 'polling' && (
              <Alert variant="info" isInline title="Charging your saved card…">
                Waiting for the payment provider to confirm the charge. This usually takes a few seconds.
              </Alert>
            )}
            <FormGroup label="Amount (EUR)" isRequired fieldId="topup-amount">
              <TextInput
                id="topup-amount"
                type="text"
                inputMode="decimal"
                value={amount}
                placeholder="100.00"
                onChange={(_event, value) => {
                  setAmount(value);
                  setAmountServerError(null);
                }}
                onBlur={() => setTouched(true)}
                validated={amountError ? 'error' : 'default'}
                isDisabled={busy}
                aria-describedby={amountError ? 'topup-amount-helper' : undefined}
              />
              {amountError && (
                <FormHelperText>
                  <HelperText id="topup-amount-helper">
                    <HelperTextItem variant="error">{amountError}</HelperTextItem>
                  </HelperText>
                </FormHelperText>
              )}
            </FormGroup>
            <FormGroup label="Pay the top-up with" fieldId="topup-source-checkout">
              <PaymentSourceChooser
                idPrefix="topup"
                allowWallet={false}
                value={source}
                onChange={setSource}
                saveCard={saveCard}
                onSaveCardChange={setSaveCard}
                amountDue={normalisedAmount}
                wallet={wallet}
                defaultPaymentMethod={defaultPaymentMethod}
                walletLoading={walletLoading}
                walletError={walletError}
                isDisabled={busy}
              />
            </FormGroup>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          isLoading={busy}
          isDisabled={busy || (touched && validationError !== null)}
        >
          {submitLabel}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TopUpModal;
